import React, { useMemo, useState } from 'react';
import { Card, Badge, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import StackedTable from '../StackedTable';
import SortableHeader from '../SortableHeader';
import SegmentedControl from '../SegmentedControl';
import { ErrorState, LoadingState } from '../PageState';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { rowClick } from '../../utils/rows';
import { EMPTY, authTypeLabel, formatDate, formatNumber, roleLabel } from '../../utils/format';

/**
 * A card for one of the company's lists, with its own loading, error and empty states so a failed
 * load never reads as "none". The count shows once the list has loaded.
 */
const ListCard = ({ title, list, loadingLabel, errorText, emptyText, headerExtra, children }) => (
  <Card className="shadow-sm">
    <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
      <h5 className="mb-0">
        {title}
        {list.loaded && <span className="text-muted fw-normal ms-2">({formatNumber(list.data.length)})</span>}
      </h5>
      {headerExtra}
    </Card.Header>
    <Card.Body className="p-0">
      {list.error ? (
        <ErrorState inCard message={errorText} onRetry={list.reload} />
      ) : !list.loaded ? (
        <LoadingState label={loadingLabel} />
      ) : list.data.length === 0 ? (
        <div className="p-4 text-center text-muted">{emptyText}</div>
      ) : (
        children
      )}
    </Card.Body>
  </Card>
);

const fullName = (user) => `${user.firstname || ''} ${user.lastname || ''}`.trim();

const timeOf = (value) => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(time) ? null : time;
};

const sortValue = (user, field) => {
  if (field === 'name') return fullName(user).toLowerCase() || null;
  if (field === 'role') return user.role ? roleLabel(user.role).toLowerCase() : null;
  return timeOf(user[field]);
};

// Dates start newest first, text A to Z
const defaultDirection = (field) => (field === 'createdAt' || field === 'deletedAt' ? 'desc' : 'asc');

/** The users table: name links to the user, sortable by name, role and dates. */
const UserTable = ({ users, deleted }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState(deleted ? 'deletedAt' : 'name');
  const [sortDirection, setSortDirection] = useState(defaultDirection(deleted ? 'deletedAt' : 'name'));

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(defaultDirection(field));
    }
  };

  const sorted = useMemo(() => {
    const factor = sortDirection === 'asc' ? 1 : -1;
    return [...users].sort((a, b) => {
      const av = sortValue(a, sortField);
      const bv = sortValue(b, sortField);
      // Missing values sort last whichever way the column is ordered
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (av < bv) return -factor;
      if (av > bv) return factor;
      return 0;
    });
  }, [users, sortField, sortDirection]);

  const sortProps = { sortField, sortDirection, onSort: handleSort };

  return (
    <StackedTable responsive className="mb-0">
      <thead className="table-light">
        <tr>
          <SortableHeader field="name" {...sortProps}>Name</SortableHeader>
          <th>Email</th>
          <SortableHeader field="role" {...sortProps}>Role</SortableHeader>
          <th className="d-none d-xl-table-cell">Sign-in</th>
          <th className="d-none d-xl-table-cell">Job title</th>
          {deleted ? (
            <SortableHeader field="deletedAt" {...sortProps}>Deleted</SortableHeader>
          ) : (
            <>
              <th>Access</th>
              <th>Email verified</th>
            </>
          )}
          <SortableHeader field="createdAt" className="d-none d-xl-table-cell" {...sortProps}>Created</SortableHeader>
        </tr>
      </thead>
      <tbody>
        {sorted.map((user) => (
          <tr
            key={user.id}
            onClick={rowClick(() => navigate(`/users/${user.id}`))}
            style={{ cursor: 'pointer' }}
            className="table-row-hover"
          >
            <td className="align-middle">
              <Link className="cell-link" to={`/users/${user.id}`}>{fullName(user) || 'No name'}</Link>
            </td>
            <td className="align-middle text-break">{user.email || EMPTY}</td>
            <td className="align-middle">{roleLabel(user.role)}</td>
            <td className="align-middle d-none d-xl-table-cell">{authTypeLabel(user.authType)}</td>
            <td className="align-middle d-none d-xl-table-cell">{user.jobTitle || EMPTY}</td>
            {deleted ? (
              <td className="align-middle text-nowrap">{formatDate(user.deletedAt)}</td>
            ) : (
              <>
                {/* Only the exceptions get a badge */}
                <td className="align-middle">
                  {user.accessPermitted ? <span className="text-muted">Permitted</span> : <Badge bg="secondary">Denied</Badge>}
                </td>
                <td className="align-middle">
                  {user.emailVerified ? <span className="text-muted">Verified</span> : <Badge bg="warning">Unverified</Badge>}
                </td>
              </>
            )}
            <td className="align-middle text-nowrap d-none d-xl-table-cell">{formatDate(user.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </StackedTable>
  );
};

const VIEW_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'deleted', label: 'Deleted' },
];

/**
 * The company's users, with deleted users as a filter of the same card. view is 'active' or
 * 'deleted'; each has its own list so they load independently.
 */
export const CompanyUsers = ({ view, onViewChange, activeUsers, deletedUsers }) => {
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const deleted = view === 'deleted';
  const list = deleted ? deletedUsers : activeUsers;
  const search = query.trim().toLowerCase();
  const matches = useMemo(() => (search
    ? list.data.filter((user) => fullName(user).toLowerCase().includes(search) || (user.email || '').toLowerCase().includes(search))
    : list.data), [list.data, search]);

  return (
    <ListCard
      title={deleted ? 'Deleted users' : 'Users'}
      list={list}
      loadingLabel={deleted ? 'Loading deleted users' : 'Loading users'}
      errorText={deleted ? 'Could not load the deleted users.' : 'Could not load the users.'}
      emptyText={deleted ? 'No deleted users for this company.' : 'No users in this company yet.'}
      headerExtra={(
        <div className={`d-flex flex-wrap align-items-center gap-2 ${isMobile ? 'w-100' : ''}`}>
          <SegmentedControl options={VIEW_OPTIONS} value={view} onChange={onViewChange} ariaLabel="Which users" />
          <Form.Control
            type="search"
            size="sm"
            placeholder="Search name or email"
            aria-label="Search users by name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: isMobile ? '100%' : 220 }}
          />
        </div>
      )}
    >
      {matches.length === 0 ? (
        <div className="p-4 text-center text-muted">No users match &quot;{query.trim()}&quot;.</div>
      ) : (
        <UserTable key={view} users={matches} deleted={deleted} />
      )}
    </ListCard>
  );
};

export const CompanyTeams = ({ teams }) => (
  <ListCard
    title="Teams"
    list={teams}
    loadingLabel="Loading teams"
    errorText="Could not load the teams."
    emptyText="No teams in this company yet."
  >
    <StackedTable responsive className="mb-0">
      <thead className="table-light">
        <tr>
          <th>Team</th>
          <th className="text-center">Members</th>
          <th>Created</th>
        </tr>
      </thead>
      <tbody>
        {teams.data.map((team) => (
          <tr key={team.id}>
            <td className="align-middle fw-semibold">{team.name}</td>
            <td className="text-center align-middle">{formatNumber(team.memberCount || 0)}</td>
            <td className="align-middle text-nowrap">{formatDate(team.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </StackedTable>
  </ListCard>
);

export const CompanyDepartments = ({ departments }) => (
  <ListCard
    title="Departments"
    list={departments}
    loadingLabel="Loading departments"
    errorText="Could not load the departments."
    emptyText="No departments in this company yet."
  >
    <StackedTable responsive className="mb-0">
      <thead className="table-light">
        <tr>
          <th>Department</th>
          <th className="text-center">People</th>
        </tr>
      </thead>
      <tbody>
        {departments.data.map((dept) => (
          <tr key={dept.id}>
            <td className="align-middle fw-semibold">{dept.name}</td>
            <td className="text-center align-middle">{formatNumber(dept.employeeCount || 0)}</td>
          </tr>
        ))}
      </tbody>
    </StackedTable>
  </ListCard>
);

export const CompanyOffices = ({ offices }) => {
  const navigate = useNavigate();
  return (
    <ListCard
      title="Offices"
      list={offices}
      loadingLabel="Loading offices"
      errorText="Could not load the offices."
      emptyText="No offices in this company yet."
    >
      <StackedTable responsive className="mb-0">
        <thead className="table-light">
          <tr>
            <th>Office</th>
            <th>Postcode</th>
            <th className="text-center">Capacity</th>
            <th className="text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {offices.data.map((office) => (
            <tr
              key={office.id}
              onClick={rowClick(() => navigate(`/offices/${office.id}`))}
              style={{ cursor: 'pointer' }}
              className="table-row-hover"
            >
              <td className="align-middle">
                <Link className="cell-link" to={`/offices/${office.id}`}>{office.label || 'Unnamed office'}</Link>
              </td>
              <td className="align-middle text-nowrap">{office.postCode || EMPTY}</td>
              <td className="text-center align-middle">{formatNumber(office.capacity || 0)}</td>
              <td className="text-center align-middle">
                {office.active ? <span className="text-muted">Active</span> : <Badge bg="secondary">Inactive</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </StackedTable>
    </ListCard>
  );
};
